import type {
  RouteMapCondition,
  RouteMapConfig,
  RouteMapHotspotActions,
  RouteMapPhotoHotspot,
  RouteMapPath,
  ScenarioContent
} from 'shared';
import type { EngineSnapshot } from '../engine/stepEngine';
import { resolveMediaRefToUrl } from './mediaRef';

export type PlayerRouteMapHotspot = RouteMapPhotoHotspot & {
  isDiscovered: boolean;
};

export type PlayerRouteMapSpot = {
  id: string;
  label: string;
  shortLabel?: string;
  description?: string;
  xPct: number;
  yPct: number;
  keyword?: string;
  ability?: string;
  relatedClueIds: string[];
  links?: {
    nodeId?: string;
    spotGateStepId?: string;
    minigameStepId?: string;
    nextStepId?: string;
  };
  photo?: {
    assetId?: string;
    url?: string;
    resolvedUrl?: string;
    caption?: string;
    hotspots: PlayerRouteMapHotspot[];
  };
  marker?: {
    icon?: string;
    color?: string;
  };
  isCurrent: boolean;
  isTarget: boolean;
  isCompleted: boolean;
  isLocked: boolean;
};

export type PlayerRouteMapPath = RouteMapPath & {
  isVisible: boolean;
};

export type PlayerRouteMap = {
  title: string;
  description?: string;
  currentIndex: number;
  targetIndex: number;
  mapImageUrl?: string;
  mapImageAlt?: string;
  coordinateSystem: 'percent';
  spots: PlayerRouteMapSpot[];
  paths: PlayerRouteMapPath[];
  /** 「現在地」ボタンの配置。未指定時は 'top-right'（従来の固定位置）。 */
  buttonPosition: 'top-left' | 'top-right';
  source: 'routeMap' | 'template19';
};

export function buildRouteMapForCurrentStep(
  scenario: ScenarioContent | null,
  snapshot: EngineSnapshot | null
): PlayerRouteMap | undefined {
  if (!scenario || !snapshot) {
    return undefined;
  }

  if (scenario.routeMap?.spots?.length) {
    return buildFromScenarioRouteMap(scenario.routeMap, scenario, snapshot);
  }

  return buildFromTemplate19Map(scenario, snapshot);
}

export function getRouteMapHotspotFlagKey(spotId: string, hotspotId: string): string {
  return `__routeMapHotspot:${spotId}:${hotspotId}`;
}

export type { RouteMapHotspotActions };

function buildFromScenarioRouteMap(
  routeMap: RouteMapConfig,
  scenario: ScenarioContent,
  snapshot: EngineSnapshot
): PlayerRouteMap | undefined {
  const spots = routeMap.spots ?? [];
  if (spots.length === 0) {
    return undefined;
  }

  const matchedStepIndex = findCurrentSpotIndexByStep(spots, snapshot);
  const stateIndex = findCurrentSpotIndexByState(routeMap, spots, snapshot);
  const initialIndex = routeMap.initialSpotId
    ? spots.findIndex((spot) => spot.id === routeMap.initialSpotId)
    : 0;
  const currentIndex = clampIndex(
    matchedStepIndex >= 0 ? matchedStepIndex : stateIndex >= 0 ? stateIndex : initialIndex,
    spots.length
  );
  const targetIndex = findTargetSpotIndex(routeMap, spots, snapshot, currentIndex);
  const flags = snapshot.state.flags ?? {};
  const mapImageUrl = routeMap.mapImage
    ? resolveMediaRefToUrl(routeMap.mapImage.assetId ?? routeMap.mapImage.url ?? '') ?? routeMap.mapImage.url
    : undefined;

  return {
    title: routeMap.title || '現在地マップ',
    description: routeMap.description,
    currentIndex,
    targetIndex,
    mapImageUrl: mapImageUrl || undefined,
    mapImageAlt: routeMap.mapImage?.alt,
    coordinateSystem: 'percent',
    buttonPosition: routeMap.ui?.buttonPosition ?? 'top-right',
    source: 'routeMap',
    spots: spots.map((spot, index) => {
      const isUnlocked = matchesRouteMapCondition(spot.status?.unlockWhen, snapshot, scenario, true);
      const isCompleted =
        index < currentIndex || matchesRouteMapCondition(spot.status?.completedWhen, snapshot, scenario, false);
      const hotspots = (spot.photo?.hotspots ?? [])
        .filter((hotspot) => matchesRouteMapCondition(hotspot.visibleWhen, snapshot, scenario, true))
        .map((hotspot) => ({
          ...hotspot,
          isDiscovered: Boolean(flags[getRouteMapHotspotFlagKey(spot.id, hotspot.id)])
        }));
      const photoRef = spot.photo?.assetId ?? spot.photo?.url;
      const resolvedPhotoUrl = photoRef ? resolveMediaRefToUrl(photoRef) ?? spot.photo?.url : undefined;

      return {
        id: spot.id || `spot-${index + 1}`,
        label: spot.label || `地点${index + 1}`,
        shortLabel: spot.shortLabel,
        description: spot.description,
        xPct: clampPercent(spot.xPct),
        yPct: clampPercent(spot.yPct),
        keyword: spot.keyword,
        ability: spot.ability,
        relatedClueIds: spot.relatedClueIds ?? [],
        links: spot.links,
        marker: spot.marker,
        photo: spot.photo
          ? {
              assetId: spot.photo.assetId,
              url: spot.photo.url,
              resolvedUrl: resolvedPhotoUrl || undefined,
              caption: spot.photo.caption,
              hotspots
            }
          : undefined,
        isCurrent: index === currentIndex,
        isTarget: index === targetIndex,
        isCompleted,
        isLocked: !isUnlocked,
      };
    }),
    paths: (routeMap.paths ?? []).map((path) => ({
      ...path,
      isVisible: matchesRouteMapCondition(path.unlockWhen, snapshot, scenario, true)
    }))
  };
}

function buildFromTemplate19Map(
  scenario: ScenarioContent,
  snapshot: EngineSnapshot
): PlayerRouteMap | undefined {
  const firstMrLiquidMinigame = scenario.nodes
    .flatMap((node) => node.steps ?? [])
    .find((step) => step.type === 'minigame' && (step as any).templateId === 19 && (step as any).config?.map);
  const baseMap = (firstMrLiquidMinigame as any)?.config?.map;
  if (!baseMap || !Array.isArray(baseMap.spots) || baseMap.spots.length === 0) {
    return undefined;
  }

  const nodeId = snapshot.node?.id ?? '';
  const stepId = snapshot.step?.id ?? '';
  const cycleMatch = `${nodeId} ${stepId}`.match(/cycle[_-]?(\d+)/i);
  const parsedCycleIndex = cycleMatch ? Number(cycleMatch[1]) - 1 : null;
  const liquidPowerCount = snapshot.state.variables?.liquid_power_count;
  const fallbackIndex = typeof liquidPowerCount === 'number' ? liquidPowerCount : 0;
  const currentIndex = clampIndex(parsedCycleIndex ?? fallbackIndex, baseMap.spots.length);

  const spots: PlayerRouteMapSpot[] = baseMap.spots.map((spot: any, index: number) => {
    const generated = generateFallbackPoint(index, baseMap.spots.length);
    return {
      id: typeof spot?.id === 'string' ? spot.id : `spot-${index + 1}`,
      label: typeof spot?.label === 'string' ? spot.label : `地点${index + 1}`,
      xPct: generated.xPct,
      yPct: generated.yPct,
      keyword: typeof spot?.keyword === 'string' ? spot.keyword : undefined,
      ability: typeof spot?.ability === 'string' ? spot.ability : undefined,
      relatedClueIds: [],
      isCurrent: index === currentIndex,
      isTarget: index === currentIndex,
      isCompleted: index < currentIndex,
      isLocked: false
    };
  });

  return {
    title: typeof baseMap.title === 'string' ? baseMap.title : '簡易マップ',
    currentIndex,
    targetIndex: currentIndex,
    coordinateSystem: 'percent',
    buttonPosition: 'top-right',
    source: 'template19',
    spots,
    paths: spots.slice(0, -1).map((spot, index) => ({
      id: `${spot.id}_to_${spots[index + 1].id}`,
      fromSpotId: spot.id,
      toSpotId: spots[index + 1].id,
      isVisible: true
    }))
  };
}

function findCurrentSpotIndexByStep(spots: RouteMapConfig['spots'], snapshot: EngineSnapshot): number {
  const nodeId = snapshot.node?.id;
  const stepId = snapshot.step?.id;
  if (!nodeId && !stepId) {
    return -1;
  }
  return spots.findIndex((spot) => {
    const links = spot.links;
    return Boolean(
      (nodeId && links?.nodeId === nodeId) ||
      (stepId && (links?.spotGateStepId === stepId || links?.minigameStepId === stepId || links?.nextStepId === stepId))
    );
  });
}

function findCurrentSpotIndexByState(
  routeMap: RouteMapConfig,
  spots: RouteMapConfig['spots'],
  snapshot: EngineSnapshot
): number {
  const variables = snapshot.state.variables ?? {};
  const spotIdKey = routeMap.state?.currentSpotIdVariable;
  if (spotIdKey && typeof variables[spotIdKey] === 'string') {
    const index = spots.findIndex((spot) => spot.id === variables[spotIdKey]);
    if (index >= 0) return index;
  }

  const indexKey = routeMap.state?.currentSpotIndexVariable;
  if (indexKey && typeof variables[indexKey] === 'number') {
    return Number(variables[indexKey]);
  }

  return -1;
}

function findTargetSpotIndex(
  routeMap: RouteMapConfig,
  spots: RouteMapConfig['spots'],
  snapshot: EngineSnapshot,
  currentIndex: number
): number {
  const variables = snapshot.state.variables ?? {};
  const targetKey = routeMap.state?.targetSpotIdVariable;
  if (targetKey && typeof variables[targetKey] === 'string') {
    const index = spots.findIndex((spot) => spot.id === variables[targetKey]);
    if (index >= 0) return index;
  }
  return currentIndex;
}

function matchesRouteMapCondition(
  condition: RouteMapCondition | undefined,
  snapshot: EngineSnapshot,
  scenario: ScenarioContent,
  defaultValue: boolean
): boolean {
  if (!condition) {
    return defaultValue;
  }
  if (condition.variableKey) {
    const value = snapshot.state.variables?.[condition.variableKey];
    if (condition.equals !== undefined) {
      return value === condition.equals;
    }
    if (condition.min !== undefined) {
      const numeric = Number(value ?? 0);
      return Number.isFinite(numeric) && numeric >= condition.min;
    }
    return Boolean(value);
  }
  if (condition.clueUnlocked) {
    const clue = scenario.clues?.find((definition) => definition.id === condition.clueUnlocked);
    const key = clue?.unlockVariable || `clue_${condition.clueUnlocked}_unlocked`;
    return Boolean(snapshot.state.variables?.[key]);
  }
  return defaultValue;
}

function generateFallbackPoint(index: number, total: number): { xPct: number; yPct: number } {
  if (total <= 1) {
    return { xPct: 50, yPct: 50 };
  }
  const t = index / Math.max(1, total - 1);
  return {
    xPct: 12 + t * 76,
    yPct: 68 - Math.sin(t * Math.PI) * 34
  };
}

function clampIndex(value: number, length: number): number {
  if (!Number.isFinite(value) || length <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(length - 1, Math.floor(value)));
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) {
    return 50;
  }
  return Math.max(0, Math.min(100, value));
}
